import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { muvekkilListeAPI, documentAPI, vekaletnameAPI, muvekkilYonetimAPI } from '../api/api';
import { davaTuruEtiketi, getStatusLabel } from '../utils/constants';
import { formatDateOnly, formatDateForDisplay } from '../utils/dateUtils';
import { waBaglanti, BURO_ADI } from '../utils/whatsapp';
import { useAuth } from '../context/AuthContext';
import { canAccessOzelDosyalar } from '../utils/ozelDosyaAccess';
import { normalizeForSearch } from '../utils/searchUtils';
import PdfGoruntuleyici from '../components/Common/PdfGoruntuleyici';
import './Muvekkiller.css';
import { onayla } from '../utils/onayla';

/**
 * Müvekkiller — vekaletlerle birlikte.
 *
 * Sistemde müvekkil ayrı bir kayıt değil; bilgiler dosyaların içinde
 * duruyor. Bu ekran dosyaları TC üzerinden gruplayıp "kaç müvekkilim var,
 * hangisinin kaç dosyası var" sorusunu cevaplıyor.
 *
 * Eskiden ayrı bir "Vekaletler" sayfası vardı; yalnızca vekaletin süreli
 * mi süresiz mi olduğunu ve aslının bulunup bulunmadığını gösteriyordu.
 * O bilgi müvekkilden ayrı düşünülemeyeceği için bu ekrana taşındı.
 */

/* Bu ekrandaki tarihler gun bazlidir (dogum, vekalet); saat gosterilmez. */
const tarih = (d) => {
  if (!d) return null;
  const s = formatDateOnly(d);
  return s === '-' ? null : s;
};

const Muvekkiller = () => {
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState('');
  const [arama, setArama] = useState('');
  const [acikTc, setAcikTc] = useState(null);
  const [detaylar, setDetaylar] = useState({});
  const [detayYukleniyor, setDetayYukleniyor] = useState(null);
  const [siralama, setSiralama] = useState('ad');
  const [belge, setBelge] = useState(null);   // { url, ad, tur } — görüntüleyici
  const [okunuyor, setOkunuyor] = useState(null);   // okunan müvekkilin TC'si
  const [okumaSonuc, setOkumaSonuc] = useState(null);
  const [taramaDurum, setTaramaDurum] = useState(null);
  const [uyusmazliklar, setUyusmazliklar] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        setVeri(await muvekkilListeAPI.liste());
      } catch (e) {
        setHata(e.response?.data?.message || e.message || 'Liste yüklenemedi.');
      } finally {
        setYukleniyor(false);
      }
    })();
  }, []);

  // Görüntüleyici kapanınca blob adresini serbest bırak.
  useEffect(() => () => { if (belge?.url) URL.revokeObjectURL(belge.url); }, [belge]);

  const muvekkiller = veri?.muvekkiller ?? veri?.Muvekkiller ?? [];

  const suzulmus = useMemo(() => {
    const a = normalizeForSearch(arama.trim());
    let liste = muvekkiller;
    if (a) {
      liste = liste.filter((m) => {
        const ad = normalizeForSearch(m.adSoyad ?? m.AdSoyad ?? '');
        const tc = String(m.tc ?? m.Tc ?? '');
        const tel = String(m.telefon ?? m.Telefon ?? '');
        return ad.includes(a) || tc.includes(arama.trim()) || tel.includes(arama.trim());
      });
    }
    const kopya = [...liste];
    if (siralama === 'dosya') {
      kopya.sort((x, y) => (y.dosyaSayisi ?? y.DosyaSayisi ?? 0) - (x.dosyaSayisi ?? x.DosyaSayisi ?? 0));
    } else if (siralama === 'vekalet') {
      // Süresi geçmiş vekaleti olanlar öne gelsin — takip gerektiren grup bu.
      const simdi = new Date(new Date().toDateString());
      const gecmisMi = (m) => {
        const t = m.vekaletTuru ?? m.VekaletTuru;
        const b = m.vekaletBitisTarihi ?? m.VekaletBitisTarihi;
        return t === 'Süreli' && b && new Date(b) < simdi ? 1 : 0;
      };
      kopya.sort((x, y) => gecmisMi(y) - gecmisMi(x));
    }
    return kopya;
  }, [muvekkiller, arama, siralama]);

  const detayAc = useCallback(async (tc) => {
    if (acikTc === tc) { setAcikTc(null); return; }
    setAcikTc(tc);
    if (detaylar[tc]) return;

    setDetayYukleniyor(tc);
    try {
      const d = await muvekkilListeAPI.detay(tc);
      setDetaylar((onceki) => ({ ...onceki, [tc]: d }));
    } catch (e) {
      setDetaylar((onceki) => ({ ...onceki, [tc]: { hata: e.response?.data?.message || e.message } }));
    } finally {
      setDetayYukleniyor(null);
    }
  }, [acikTc, detaylar]);

  /* Detaydaki "Girişi durdur / Girişi aç" listedeki satıra da yansısın. */
  const girisDurumuDegisti = useCallback((tc, durduruldu) => {
    setVeri((v) => {
      if (!v) return v;
      const anahtar = v.muvekkiller ? 'muvekkiller' : 'Muvekkiller';
      return {
        ...v,
        [anahtar]: (v[anahtar] || []).map((m) => ((m.tc ?? m.Tc) === tc
          ? { ...m, girisDurduruldu: durduruldu, GirisDurduruldu: durduruldu }
          : m)),
      };
    });
  }, []);

  /** Vekaletnameyi indirip görüntüleyicide açar. */
  const vekaletnameAc = useCallback(async (v) => {
    const id = v.id ?? v.Id;
    const ad = v.fileName ?? v.FileName ?? 'Vekaletname';
    const tip = String(v.contentType ?? v.ContentType ?? '');
    setBelge({ url: null, ad, tur: tip.startsWith('image/') ? 'gorsel' : 'pdf', yukleniyor: true });
    try {
      const sonuc = await documentAPI.download(id);
      const blob = sonuc?.blob ?? sonuc;
      const url = URL.createObjectURL(blob);
      setBelge({ url, ad, tur: tip.startsWith('image/') ? 'gorsel' : 'pdf' });
    } catch (e) {
      setBelge({ url: null, ad, hata: e.response?.data?.message || e.message || 'Belge açılamadı.' });
    }
  }, []);

  const uyusmazliklariYukle = useCallback(async () => {
    try {
      const v = await vekaletnameAPI.uyusmazliklar('bekliyor');
      setUyusmazliklar(v.liste ?? v.Liste ?? []);
    } catch { /* sessiz — ekranın geri kalanı çalışmaya devam etsin */ }
  }, []);

  const durumuTazele = useCallback(async () => {
    try {
      const d = await vekaletnameAPI.taramaDurum();
      setTaramaDurum(d);
      return d;
    } catch { return null; }
  }, []);

  useEffect(() => { durumuTazele(); uyusmazliklariYukle(); }, [durumuTazele, uyusmazliklariYukle]);

  // Tarama sürerken ilerlemeyi izle.
  useEffect(() => {
    if (!taramaDurum?.calisiyor && !taramaDurum?.Calisiyor) return undefined;
    const t = setInterval(async () => {
      const d = await durumuTazele();
      if (d && !(d.calisiyor ?? d.Calisiyor)) {
        clearInterval(t);
        uyusmazliklariYukle();
        // Tarama bitince liste tazelensin, yeni bilgiler görünsün.
        try { setVeri(await muvekkilListeAPI.liste()); } catch { /* yut */ }
        setDetaylar({});
      }
    }, 4000);
    return () => clearInterval(t);
  }, [taramaDurum, durumuTazele, uyusmazliklariYukle]);

  const taramayiBaslat = useCallback(async () => {
    try {
      const s = await vekaletnameAPI.taramaBaslat(false);
      setTaramaDurum(s.durum ?? s.Durum ?? null);
      if (!(s.basladi ?? s.Basladi)) {
        window.alert('Tarama zaten çalışıyor.');
      }
    } catch (e) {
      window.alert('Tarama başlatılamadı: ' + (e.response?.data?.message || e.message));
    }
  }, []);

  const uyusmazlikKarar = useCallback(async (id, onayla) => {
    try {
      await vekaletnameAPI.uyusmazlikKarar(id, onayla);
      await uyusmazliklariYukle();
      if (onayla) {
        try { setVeri(await muvekkilListeAPI.liste()); } catch { /* yut */ }
        setDetaylar({});
      }
    } catch (e) {
      window.alert('İşlenemedi: ' + (e.response?.data?.message || e.message));
    }
  }, [uyusmazliklariYukle]);

  /** Müvekkilin vekaletnamelerini okuyup boş alanları doldurur. */
  const vekaletnamedenOku = useCallback(async (tc) => {
    setOkunuyor(tc);
    setOkumaSonuc(null);
    try {
      const s = await vekaletnameAPI.muvekkilOku(tc);
      const yazilan = s.yazilan ?? s.Yazilan ?? [];
      setOkumaSonuc({
        tc,
        basarili: !!(s.tamam ?? s.Tamam),
        hata: s.hata ?? s.Hata,
        yazilan,
        okunanBelge: s.okunanBelge ?? s.OkunanBelge ?? 0,
      });
      if (yazilan.length > 0) {
        // Yazılan bilgiler ekrana yansısın diye detayı tazeliyoruz.
        const d = await muvekkilListeAPI.detay(tc);
        setDetaylar((onceki) => ({ ...onceki, [tc]: d }));
      }
    } catch (e) {
      setOkumaSonuc({ tc, basarili: false, hata: e.response?.data?.message || e.message });
    } finally {
      setOkunuyor(null);
    }
  }, []);

  if (yukleniyor) {
    return (
      <div className="mv-container">
        <header className="mv-header"><h1>Müvekkiller</h1></header>
        <p className="mv-bos">Yükleniyor…</p>
      </div>
    );
  }

  if (hata) {
    return (
      <div className="mv-container">
        <header className="mv-header"><h1>Müvekkiller</h1></header>
        <p className="mv-bos mv-hata">{hata}</p>
      </div>
    );
  }

  const muvekkilSayisi = veri?.muvekkilSayisi ?? veri?.MuvekkilSayisi ?? 0;
  const dosyaSayisi = veri?.dosyaSayisi ?? veri?.DosyaSayisi ?? 0;
  const tcsiz = veri?.tcsizDosya ?? veri?.TcsizDosya ?? 0;
  // Süresi geçmiş vekaleti olan müvekkil sayısı (yaklaşanlar sayılmaz).
  const bugunBas = new Date(new Date().toDateString());
  const gecmisToplam = muvekkiller.filter((m) => {
    const t = m.vekaletTuru ?? m.VekaletTuru;
    const b = m.vekaletBitisTarihi ?? m.VekaletBitisTarihi;
    return t === 'Süreli' && b && new Date(b) < bugunBas;
  }).length;

  return (
    <div className="mv-container">
      <header className="mv-header">
        <div className="mv-header-sol">
          <h1>Müvekkiller</h1>
          <span className="mv-alt">
            Dosyalardaki TC numaralarına göre gruplandı. Vekalet bilgileri de
            burada — ayrı bir Vekaletler sayfası yok.
          </span>
        </div>
        <div className="mv-header-sag">
          <button
            type="button"
            className="mv-tarama-dugme"
            onClick={taramayiBaslat}
            disabled={!!(taramaDurum?.calisiyor ?? taramaDurum?.Calisiyor)}
          >
            {(taramaDurum?.calisiyor ?? taramaDurum?.Calisiyor)
              ? 'Tarama sürüyor…'
              : 'Tüm vekaletnameleri tara'}
          </button>
        </div>
      </header>

      {(taramaDurum?.calisiyor ?? taramaDurum?.Calisiyor) && (
        <div className="mv-tarama-serit">
          <div className="mv-tarama-bilgi">
            <strong>Vekaletnameler taranıyor</strong>
            <span>
              {(taramaDurum.islenen ?? taramaDurum.Islenen ?? 0)} / {(taramaDurum.toplam ?? taramaDurum.Toplam ?? 0)} dosya
              {' · '}{(taramaDurum.yazilanDosya ?? taramaDurum.YazilanDosya ?? 0)} dosyaya bilgi yazıldı
              {(taramaDurum.suAnki ?? taramaDurum.SuAnki) ? ' · ' + (taramaDurum.suAnki ?? taramaDurum.SuAnki) : ''}
            </span>
          </div>
          <div className="mv-tarama-cubuk">
            <span style={{
              width: ((taramaDurum.toplam ?? taramaDurum.Toplam)
                ? ((taramaDurum.islenen ?? taramaDurum.Islenen) / (taramaDurum.toplam ?? taramaDurum.Toplam)) * 100
                : 0) + '%',
            }} />
          </div>
          <p className="mv-tarama-not">
            Bir belge 3–60 saniyede okunuyor; işlem arka planda sürüyor,
            sayfadan ayrılabilirsiniz.
          </p>
        </div>
      )}

      {uyusmazliklar.length > 0 && (
        <section className="mv-uyusmazlik">
          <header className="mv-uyusmazlik-basi">
            <h2>Onayınızı bekleyen {uyusmazliklar.length} kayıt</h2>
            <p>
              Vekaletnameden okunan kimlik bilgisi sistemdekinden farklı.
              Hiçbiri değiştirilmedi — kararı siz verin. TC numaralarının
              yanındaki işaret resmî doğrulama sonucudur: <strong>geçersiz</strong>
              yazan numara hatalı yazılmış demektir.
            </p>
          </header>
          <div className="mv-tablo-kap">
            <table className="mv-tablo">
              <thead>
                <tr>
                  <th>Föy</th><th>Müvekkil</th><th>Alan</th>
                  <th>Sistemdeki</th><th>Vekaletnamede</th><th />
                </tr>
              </thead>
              <tbody>
                {uyusmazliklar.map((u) => {
                  const id = u.id ?? u.Id;
                  const sistemGecerli = u.sistemGecerli ?? u.SistemGecerli ?? null;
                  const okunanGecerli = u.okunanGecerli ?? u.OkunanGecerli ?? null;
                  // Sistemdeki numara doğrulamadan geçmiyorsa satır öne çıksın.
                  const oncelikli = sistemGecerli === false && okunanGecerli === true;
                  return (
                    <tr key={id} className={oncelikli ? 'mv-oncelikli' : undefined}>
                      <td className="sayisal">{u.foyNo ?? u.FoyNo ?? '—'}</td>
                      <td>{u.muvekkil ?? u.Muvekkil ?? '—'}</td>
                      <td>{(u.alan ?? u.Alan) === 'tc' ? 'TC Kimlik No' : 'Ad Soyad'}</td>
                      <td className="mv-eski">
                        {u.sistemdekiDeger ?? u.SistemdekiDeger ?? '—'}
                        {sistemGecerli === false && <span className="mv-tc-uyari">geçersiz</span>}
                        {sistemGecerli === true && <span className="mv-tc-tamam">geçerli</span>}
                      </td>
                      <td className="mv-yeni">
                        {u.okunanDeger ?? u.OkunanDeger ?? '—'}
                        {okunanGecerli === false && <span className="mv-tc-uyari">geçersiz</span>}
                        {okunanGecerli === true && <span className="mv-tc-tamam">geçerli</span>}
                      </td>
                      <td className="mv-git">
                        <div className="mv-satir-islem">
                          <button type="button" className="mv-karar-red"
                            onClick={() => uyusmazlikKarar(id, false)}>Yoksay</button>
                          <button type="button" className="mv-karar-onay"
                            onClick={() => uyusmazlikKarar(id, true)}>Uygula</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="mv-ozet">
        <div className="mv-ozet-kart">
          <span className="mv-ozet-sayi">{muvekkilSayisi}</span>
          <span className="mv-ozet-ad">müvekkil</span>
        </div>
        <div className="mv-ozet-kart">
          <span className="mv-ozet-sayi">{dosyaSayisi}</span>
          <span className="mv-ozet-ad">dosya</span>
        </div>
        <div className="mv-ozet-kart">
          <span className="mv-ozet-sayi">
            {muvekkilSayisi > 0 ? (dosyaSayisi / muvekkilSayisi).toFixed(1).replace('.', ',') : '—'}
          </span>
          <span className="mv-ozet-ad">müvekkil başına dosya</span>
        </div>
        {gecmisToplam > 0 && (
          <div className="mv-ozet-kart mv-uyari">
            <span className="mv-ozet-sayi">{gecmisToplam}</span>
            <span className="mv-ozet-ad">süresi geçmiş vekalet</span>
          </div>
        )}
        {tcsiz > 0 && (
          <div className="mv-ozet-kart mv-uyari">
            <span className="mv-ozet-sayi">{tcsiz}</span>
            <span className="mv-ozet-ad">TC'si olmadığı için gruplanamayan dosya</span>
          </div>
        )}
      </section>

      <div className="mv-arac">
        <input
          type="search"
          className="form-control mv-arama"
          value={arama}
          onChange={(e) => setArama(e.target.value)}
          placeholder="İsim, TC veya telefon ara…"
        />
        <div className="mv-siralama">
          <button type="button" className={siralama === 'ad' ? 'aktif' : ''} onClick={() => setSiralama('ad')}>
            İsme göre
          </button>
          <button type="button" className={siralama === 'dosya' ? 'aktif' : ''} onClick={() => setSiralama('dosya')}>
            Dosya sayısı
          </button>
          <button type="button" className={siralama === 'vekalet' ? 'aktif' : ''} onClick={() => setSiralama('vekalet')}>
            Vekalet süresi
          </button>
        </div>
      </div>

      {suzulmus.length === 0 ? (
        <p className="mv-bos">Aramanıza uyan müvekkil yok.</p>
      ) : (
        <>
          <p className="mv-sayac">{suzulmus.length} müvekkil listeleniyor</p>

          {/* Sütun başlıkları — liste satırıyla aynı ızgarada durur */}
          <div className="mv-liste-basi" aria-hidden="true">
            <span />
            <span>Müvekkil</span>
            <span>TC Kimlik No</span>
            <span>Telefon</span>
            <span>Vekalet</span>
            <span className="mv-sag">Dosya</span>
          </div>

          <ul className="mv-liste">
            {suzulmus.map((m) => {
              const tc = m.tc ?? m.Tc;
              const acikMi = acikTc === tc;
              const dosyaAdet = m.dosyaSayisi ?? m.DosyaSayisi ?? 0;
              const vekTur = m.vekaletTuru ?? m.VekaletTuru;
              // Yalnızca süresi GEÇMİŞ olanlar kırmızı. Yaklaşanlar
              // renk değiştirmiyor — kullanıcı böyle istedi.
              const bitis = m.vekaletBitisTarihi ?? m.VekaletBitisTarihi;
              const suresiGecmis = vekTur === 'Süreli' && bitis
                && new Date(bitis) < new Date(new Date().toDateString());
              return (
                <li key={tc} className={'mv-satir' + (acikMi ? ' acik' : '')}>
                  <button type="button" className="mv-satir-bas" onClick={() => detayAc(tc)}>
                    <span className={'mv-ok' + (acikMi ? ' acik' : '')} aria-hidden="true" />
                    <span className="mv-ad">
                      {m.adSoyad ?? m.AdSoyad}
                      {(m.sirket ?? m.Sirket) && <span className="mv-sirket">şirket</span>}
                      {(m.girisDurduruldu ?? m.GirisDurduruldu) && (
                        <span className="mv-durduruldu" title="Uygulamaya ve siteye girişi durduruldu">giriş durduruldu</span>
                      )}
                    </span>
                    <span className="mv-tc">{tc}</span>
                    <span className="mv-tel">{m.telefon ?? m.Telefon ?? '—'}</span>
                    <span className="mv-vekalet-hucre">
                      {vekTur ? (
                        <>
                          <span className={'mv-vek-tur' + (vekTur === 'Süreli' ? ' sureli' : '')}>{vekTur}</span>
                          {vekTur === 'Süreli' && (
                            <span className={'mv-vek-tarih' + (suresiGecmis ? ' gecmis' : '')}>
                              {tarih(m.vekaletBitisTarihi ?? m.VekaletBitisTarihi) || 'tarih yok'}
                            </span>
                          )}
                        </>
                      ) : <span className="mv-vek-yok">—</span>}
                    </span>
                    <span className={'mv-dosya-adet' + (dosyaAdet > 1 ? ' coklu' : '')}>
                      {dosyaAdet}
                    </span>
                  </button>

                  {acikMi && (
                    <MuvekkilDetay
                      ozet={m}
                      detay={detaylar[tc]}
                      yukleniyor={detayYukleniyor === tc}
                      onVekaletnameAc={vekaletnameAc}
                      onOku={() => vekaletnamedenOku(tc)}
                      okunuyor={okunuyor === tc}
                      okumaSonuc={okumaSonuc?.tc === tc ? okumaSonuc : null}
                      onGirisDurumu={girisDurumuDegisti}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {belge && (
        <BelgePenceresi belge={belge} onKapat={() => setBelge(null)} />
      )}
    </div>
  );
};

/** Açılan müvekkil: tüm bilgiler + dosya/vekalet listesi. */
const MuvekkilDetay = ({ ozet, detay, yukleniyor, onVekaletnameAc, onOku, okunuyor, okumaSonuc, onGirisDurumu }) => {
  /* UYGULAMA / SİTE GİRİŞİ: büro müvekkilin girişini durdurup sonra
     yeniden açabilir. Durdurunca açık oturumu hemen kapanır; açınca eski
     şifresiyle girer. Giriş kodu gibi yalnızca yetkili hesapta görünür. */
  const { user } = useAuth();
  const yetkili = canAccessOzelDosyalar(user);
  const girisTc = ozet?.tc ?? ozet?.Tc;
  const [giris, setGiris] = useState(null);
  const [girisIslem, setGirisIslem] = useState(false);
  const [girisHata, setGirisHata] = useState('');

  useEffect(() => {
    if (!girisTc || !yetkili) return undefined;
    let iptal = false;
    muvekkilYonetimAPI.girisDurumu(girisTc)
      .then((d) => { if (!iptal) setGiris(d); })
      .catch(() => { if (!iptal) setGiris(null); });
    return () => { iptal = true; };
  }, [girisTc, yetkili]);

  const durduruldu = !!(giris?.girisDurduruldu ?? giris?.GirisDurduruldu);

  const girisiDegistir = async () => {
    if (!girisTc || girisIslem) return;
    const durdur = !durduruldu;
    if (durdur && !await onayla(
      'Müvekkilin girişi durdurulsun mu?\n\n'
      + 'Uygulamadan ve siteden hemen çıkarılır; şifresiyle, giriş koduyla ya da Face ID ile giremez. '
      + 'Telefonuna bildirim de gitmez.\n\n'
      + 'İstediğiniz zaman "Girişi aç" ile eskisi gibi girebilir; şifresi silinmez.',
      { onay: 'Girişi durdur', tehlikeli: true })) return;

    setGirisIslem(true);
    setGirisHata('');
    try {
      const d = durdur
        ? await muvekkilYonetimAPI.girisDurdur(girisTc)
        : await muvekkilYonetimAPI.girisAc(girisTc);
      setGiris(d);
      onGirisDurumu?.(girisTc, !!(d?.girisDurduruldu ?? d?.GirisDurduruldu));
    } catch (e) {
      setGirisHata(e?.response?.data?.message || 'İşlem yapılamadı. Tekrar deneyin.');
    } finally {
      setGirisIslem(false);
    }
  };

  /* Tek kullanımlık giriş kodu.
     Müvekkilin şifresi bcrypt ile saklandığı için geri okunamaz; giremeyen
     müvekkile yeni kod üretiliyor. Kod yalnızca üretildiği anda görünür. */
  const [kod, setKod] = useState(null);
  const [kodUretiliyor, setKodUretiliyor] = useState(false);

  const kodUret = async () => {
    const tc = ozet?.tc ?? ozet?.Tc;
    if (!tc || kodUretiliyor) return;
    if (!await onayla(
      'Yeni giriş kodu üretilsin mi?\n\n'
      + 'Müvekkilin mevcut şifresi GEÇERSİZ olur ve uygulamadaki açık oturumu kapanır; '
      + 'yeni kodla girip kendi şifresini yeniden belirlemesi gerekir.')) return;

    setKodUretiliyor(true);
    try {
      const d = await muvekkilYonetimAPI.girisKoduUret(tc);
      setKod(d);
    } catch (e) {
      window.alert(e?.response?.data?.message || 'Giriş kodu üretilemedi.');
    } finally {
      setKodUretiliyor(false);
    }
  };

  /* WhatsApp bağlantısı — kod mesajda yazılı gider; müvekkil bu kodla
     girip kendi şifresini belirleyeceği için kod tek kullanımlık. */
  const waBaglantisi = (() => {
    if (!kod) return null;
    const tel = detay?.telefon ?? detay?.Telefon ?? ozet?.telefon ?? ozet?.Telefon;
    return waBaglanti(tel,
      `Sayın ${kod.adSoyad || ''}, dosyalarınızı takip edebileceğiniz uygulamamıza `
      + `T.C. kimlik numaranız ve şu giriş kodu ile girebilirsiniz:\n\n${kod.kod}\n\n`
      + `İlk girişten sonra kendi şifrenizi belirleyeceksiniz. Kod ${kod.gecerlilikGun} gün geçerlidir.\n\n`
      + `https://izzetbaranozturk.com\n\n${BURO_ADI}`);
  })();

  if (yukleniyor) return <div className="mv-detay"><p className="mv-bos">Yükleniyor…</p></div>;
  if (detay?.hata) return <div className="mv-detay"><p className="mv-bos mv-hata">{detay.hata}</p></div>;

  const m = detay?.muvekkil ?? detay?.Muvekkil ?? ozet;
  const dosyalar = detay?.dosyalar ?? detay?.Dosyalar ?? [];
  const yazimlar = (m.digerYazimlar ?? m.DigerYazimlar ?? []).filter(
    (y) => y !== (m.adSoyad ?? m.AdSoyad)
  );

  const vekTuru = m.vekaletTuru ?? m.VekaletTuru;
  const verilis = tarih(m.vekaletVerilisTarihi ?? m.VekaletVerilisTarihi);
  const bitis = tarih(m.vekaletBitisTarihi ?? m.VekaletBitisTarihi);

  const alanlar = [
    ['TC Kimlik No', m.tc ?? m.Tc],
    ['Doğum tarihi', tarih(m.dogumTarihi ?? m.DogumTarihi)],
    ['Doğum yeri', m.dogumYeri ?? m.DogumYeri],
    ['Baba adı', m.babaAdi ?? m.BabaAdi],
    ['Ana adı', m.anaAdi ?? m.AnaAdi],
    ['Telefon', m.telefon ?? m.Telefon],
    ['E-posta', m.eposta ?? m.Eposta],
    ['Adres', m.adres ?? m.Adres],
    ['IBAN', m.iban ?? m.Iban],
    ['Vekalet türü', vekTuru],
    ['Vekalet veriliş tarihi', verilis],
    // Süresiz vekalette bitiş tarihi diye bir şey yok; satırı hiç göstermiyoruz.
    ...(vekTuru === 'Süreli' ? [['Vekalet bitiş tarihi', bitis]] : []),
    ['Noter', m.noterAdi ?? m.NoterAdi],
    ['Yevmiye no', m.yevmiyeNo ?? m.YevmiyeNo],
  ];

  return (
    <div className="mv-detay">
      <div className="mv-bilgi-basi">
        <span className="mv-bilgi-basi-ad">Müvekkil Bilgileri</span>
        <div className="mv-bilgi-dugmeler">
          <button type="button" className="mv-oku-dugme" onClick={onOku} disabled={okunuyor}>
            {okunuyor ? 'Vekaletname okunuyor…' : 'Vekaletnameden oku'}
          </button>
          <button type="button" className="mv-kod-dugme" onClick={kodUret} disabled={kodUretiliyor || durduruldu}
            title={durduruldu ? 'Girişi durdurulmuş müvekkile kod üretilmez; önce girişi açın.' : undefined}>
            {kodUretiliyor ? 'Üretiliyor…' : 'Giriş kodu üret'}
          </button>
        </div>
      </div>

      {yetkili && giris && (
        <div className={'mv-giris' + (durduruldu ? ' durduruldu' : '')} role="status">
          <span className="mv-giris-isaret" aria-hidden="true" />
          <div className="mv-giris-metin">
            <strong>{durduruldu ? 'Uygulama girişi durduruldu' : 'Uygulama girişi açık'}</strong>
            <span>
              {durduruldu
                ? `${formatDateForDisplay(giris.durdurmaTarihi ?? giris.DurdurmaTarihi)}`
                  + `${(giris.durduran ?? giris.Durduran) ? ` · ${giris.durduran ?? giris.Durduran}` : ''}`
                  + ' — müvekkil uygulamaya ve siteye giremiyor.'
                : 'Müvekkil uygulamaya ve siteye girebiliyor.'}
            </span>
          </div>
          <button type="button" className={'mv-giris-dugme' + (durduruldu ? ' ac' : '')}
            onClick={girisiDegistir} disabled={girisIslem}>
            {girisIslem ? 'Bekleyin…' : durduruldu ? 'Girişi aç' : 'Girişi durdur'}
          </button>
          {girisHata && <p className="mv-giris-hata">{girisHata}</p>}
        </div>
      )}

      {/* Üretilen kod YALNIZCA burada görünür; sayfadan çıkınca bir daha
          gösterilmez. Gerekirse yenisini üretmek saniyelik iş. */}
      {kod && (
        <div className="mv-kod-kutu">
          <div className="mv-kod-ust">
            <span className="mv-kod-etiket">Tek kullanımlık giriş kodu</span>
            <button type="button" className="mv-kod-kapat" onClick={() => setKod(null)} aria-label="Kapat">×</button>
          </div>
          <strong className="mv-kod-deger">{kod.kod}</strong>
          <p className="mv-kod-not">
            {kod.gecerlilikGun} gün geçerli. Müvekkil bu kodla girip <b>kendi şifresini</b>
            {' '}belirleyecek; kod o anda geçersizleşir. Eski şifresi artık çalışmaz.
          </p>
          {waBaglantisi && (
            <a className="mv-kod-wa" href={waBaglantisi} target="_blank" rel="noopener noreferrer">
              WhatsApp ile gönder
            </a>
          )}
        </div>
      )}

      {okumaSonuc && (
        <p className={'mv-oku-sonuc' + (okumaSonuc.basarili && okumaSonuc.yazilan?.length ? ' basarili' : okumaSonuc.basarili ? '' : ' hata')}>
          {!okumaSonuc.basarili
            ? (okumaSonuc.hata || 'Vekaletname okunamadı.')
            : okumaSonuc.yazilan?.length
              ? 'Okundu ve dolduruldu: ' + okumaSonuc.yazilan.join(', ') + '.'
              : 'Vekaletname okundu ama yeni bir bilgi çıkmadı (alanlar zaten dolu olabilir).'}
        </p>
      )}

      <div className="mv-bilgi-izgara">
        {alanlar.map(([ad, deger]) => (
          <div className="mv-bilgi" key={ad}>
            <span className="mv-bilgi-ad">{ad}</span>
            <span className={'mv-bilgi-deger' + (deger ? '' : ' bos')}>
              {deger || 'girilmemiş'}
            </span>
          </div>
        ))}
      </div>

      {yazimlar.length > 0 && (
        <p className="mv-yazim">
          <strong>Dosyalarda farklı yazılmış:</strong> {yazimlar.join(' · ')}
        </p>
      )}

      <h4 className="mv-dosya-basi">Dosyaları ve vekaletleri ({dosyalar.length})</h4>
      <div className="mv-tablo-kap">
        <table className="mv-tablo">
          <thead>
            <tr>
              <th>Föy</th>
              <th>Ad Soyad</th>
              <th>Dava Türü</th>
              <th>Durum</th>
              <th>Vekalet</th>
              <th>Aslı</th>
              <th>Sorumlu</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {dosyalar.map((d) => {
              const id = d.id ?? d.Id;
              const v = d.vekalet ?? d.Vekalet;
              const vt = v?.vekaletTuru ?? v?.VekaletTuru;
              const vb = tarih(v?.vekaletBitisTarihi ?? v?.VekaletBitisTarihi);
              const asli = v ? (v.vekaletinAsliVar ?? v.VekaletinAsliVar) : null;
              const vn = d.vekaletname ?? d.Vekaletname;
              return (
                <tr key={id}>
                  <td className="sayisal">{d.foyNo ?? d.FoyNo ?? '—'}</td>
                  <td>{d.adSoyad ?? d.AdSoyad ?? '—'}</td>
                  <td>
                    {davaTuruEtiketi({
                      DavaTuru: d.davaTuru ?? d.DavaTuru,
                      DavaTuruDiger: d.davaTuruDiger ?? d.DavaTuruDiger,
                    }) || '—'}
                  </td>
                  <td>{getStatusLabel(d.status ?? d.Status) || '—'}</td>
                  <td>
                    {vt ? (
                      <>
                        {vt}
                        {vt === 'Süreli' && vb && <span className="mv-vek-bitis"> · {vb}</span>}
                      </>
                    ) : '—'}
                  </td>
                  <td>
                    {asli === null ? '—' : (
                      <span className={'mv-asli' + (asli ? ' var' : '')}>{asli ? 'var' : 'yok'}</span>
                    )}
                  </td>
                  <td>{d.sorumlu ?? d.Sorumlu ?? '—'}</td>
                  <td className="mv-git">
                    <div className="mv-satir-islem">
                      {vn && (
                        <button
                          type="button"
                          className="mv-vekaletname-dugme"
                          onClick={() => onVekaletnameAc(vn)}
                        >
                          Vekaletname
                        </button>
                      )}
                      <Link to={`/case-files/${id}`} className="mv-git-baglanti">Dosya →</Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** Vekaletname görüntüleyici. */
const BelgePenceresi = ({ belge, onKapat }) => (
  <div className="mv-belge-perde" onClick={onKapat}>
    <div className="mv-belge-pencere" onClick={(e) => e.stopPropagation()}>
      <header className="mv-belge-basi">
        <span className="mv-belge-ad">{belge.ad}</span>
        <button type="button" className="mv-belge-kapat" onClick={onKapat} aria-label="Kapat">×</button>
      </header>
      <div className="mv-belge-govde">
        {belge.hata ? (
          <p className="mv-bos mv-hata">{belge.hata}</p>
        ) : !belge.url ? (
          <p className="mv-bos">Belge açılıyor…</p>
        ) : belge.tur === 'gorsel' ? (
          /* Gorsel de kendi kutusunda kaydirilir. */
          <div className="mv-belge-gorsel-kap">
            <img src={belge.url} alt={belge.ad} className="mv-belge-gorsel" />
          </div>
        ) : (
          /* Görüntüleyici yüksekliğini ebeveyninden alıyor; bu yüzden
             saran kutunun belirli bir yüksekliği olmalı, yoksa sayfa
             aşağı kaydırılamıyor. */
          <div className="mv-belge-pdf">
            <PdfGoruntuleyici kaynak={belge.url} dosyaAdi={belge.ad} />
          </div>
        )}
      </div>
    </div>
  </div>
);

export default Muvekkiller;
